// ================================================================
// 每日全人生活教練 API 模組
// ================================================================
// 版本：1.0 (v1.3)
// 功能：每日飲食/穿搭/學習/運動/自省建議 + 學習進度管理
// ================================================================

import apiClient from './client';

// ================================================================
// 型別定義
// ================================================================

export interface DailyFoodItem {
  id: number;
  meal_type: 'breakfast' | 'lunch' | 'dinner';
  food_name: string;
  description: string;
  body_goal: string;
  season: string;
  calories_approx: number | null;
  prep_time_min: number | null;
  tags: string | null;
}

export interface DailyClothingItem {
  id: number;
  season: string;
  style_category: string;
  outfit_name: string;
  top: string;
  bottom: string;
  shoes: string;
  accessories: string | null;
  color_scheme: string | null;
  occasion: string | null;
  ziwei_gong: string | null;
  ziwei_lucky_color: string | null;
}

export interface DailyExerciseItem {
  id: number;
  activity_name: string;
  activity_type: string;
  duration_min: number | null;
  intensity_level: string | null;
  description: string | null;
  benefits: string | null;
  season: string;
  time_slot: string | null;
  beginner_friendly: number;
}

export interface DailyReflectionItem {
  id: number;
  category: string;
  question: string;
  prompt_detail: string | null;
  example_answer: string | null;
}

export interface DailyLearningTopic {
  series_id: number;
  series_name: string;
  topic_order: number;
  topic_name: string | null;
  content: string | null;
  key_insight: string | null;
  action_step: string | null;
  is_new_series: boolean;
}

export interface SolarTermDrink {
  term_name: string;
  drink_name: string;
  ingredient_list: string | null;
  description: string | null;
}

export interface DailySchedule {
  date: string;
  season: string;
  breakfast: DailyFoodItem | null;
  lunch: DailyFoodItem | null;
  dinner: DailyFoodItem | null;
  clothing: DailyClothingItem | null;
  learning: DailyLearningTopic[];
  exercise: DailyExerciseItem | null;
  reflection: DailyReflectionItem | null;
  solar_term_drink: SolarTermDrink | null;
}

export interface PalaceFocus {
  food: string;
  clothing: string;
  learning: string;
  exercise: string;
  reflection: string;
}

/** 單一宮位運勢洞察（最佳或最差） */
export interface PalaceAdvice {
  palace: string;      // 宮位名稱，如 "財帛宮"
  score: number;       // 分數
  theme: string;       // 主題關鍵字，如 "理財進帳"
  advice: string;      // 制式建議文字
}

/** 今日流日 12 宮運勢分析 */
export interface DailyPalaceInsight {
  scores: Record<string, number>;  // 12 宮完整分數
  best: PalaceAdvice;              // 運勢最佳宮
  worst: PalaceAdvice;             // 運勢最差宮
  flow_day_palace: string;         // 今日流日命宮位置（如 "辰宮"）
  lunar_day: number;               // 農曆日（初幾）
}

export interface DailyLifeResponse {
  success: boolean;
  date: string;
  season: 'spring' | 'summer' | 'autumn' | 'winter';
  lowest_palace: string;
  palace_focus: PalaceFocus;
  palace_insight: DailyPalaceInsight | null;
  daily: DailySchedule;
  metadata: {
    birth_time: string;
    gender: string;
  };
  error?: string;
}

export interface LearningSeries {
  id: number;
  series_name: string;
  category: string;
  description: string | null;
  total_topics: number;
  difficulty: string | null;
  ziwei_gong: string | null;
}

export interface LearningProgress {
  id: number;
  user_id_key: string;
  series_id: number;
  series_name: string;
  category: string;
  total_topics: number;
  description: string | null;
  current_step: number;
  started_at: string | null;
  last_studied_at: string | null;
  completed_at: string | null;
  total_days_studied: number;
}

export interface LearningStatusResponse {
  success: boolean;
  active_progress: LearningProgress[];
  all_series: LearningSeries[];
  error?: string;
}

export interface StartSeriesResponse {
  success: boolean;
  message: string;
}

export interface UpdateProgressResponse {
  success: boolean;
  step: number;
  completed: boolean;
  message: string;
}

// ================================================================
// API 呼叫函數
// ================================================================

/**
 * 取得今日全人生活建議
 * GET /api/daily-life?target_date=YYYY-MM-DD
 */
export const getDailyLifeAdvice = async (
  targetDate?: string
): Promise<DailyLifeResponse> => {
  const params: Record<string, string> = {};
  if (targetDate) {
    params.target_date = targetDate;
  }
  const response = await apiClient.get('/api/daily-life', { params });
  return response as unknown as DailyLifeResponse;
};

/**
 * 取得學習系列列表與個人進度
 * GET /api/learning/series
 */
export const getLearningSeries = async (): Promise<LearningStatusResponse> => {
  const response = await apiClient.get('/api/learning/series');
  return response as unknown as LearningStatusResponse;
};

/**
 * 開始一個學習系列
 * POST /api/learning/series/:series_id/start
 */
export const startLearningSeries = async (
  seriesId: number
): Promise<StartSeriesResponse> => {
  const response = await apiClient.post(`/api/learning/series/${seriesId}/start`);
  return response as unknown as StartSeriesResponse;
};

/**
 * 放棄學習系列
 * POST /api/learning/series/:id/abandon
 */
export const abandonLearningSeries = async (
  seriesId: number
): Promise<StartSeriesResponse> => {
  const response = await apiClient.post(`/api/learning/series/${seriesId}/abandon`);
  return response as unknown as StartSeriesResponse;
};

/**
 * 完成今日學習，更新進度
 * POST /api/learning/progress
 */
export const completeTodayLearning = async (
  seriesId: number
): Promise<UpdateProgressResponse> => {
  const response = await apiClient.post('/api/learning/progress', null, {
    params: { series_id: seriesId },
  });
  return response as unknown as UpdateProgressResponse;
};

// ================================================================
// 工具函數
// ================================================================

/**
 * 季節中文名稱
 */
export const getSeasonLabel = (season: string): string => {
  const map: Record<string, string> = {
    spring: '春',
    summer: '夏',
    autumn: '秋',
    winter: '冬',
  };
  return map[season] ?? season;
};

/**
 * 宮位對應的主題色（用於 UI 高亮）
 */
export const getPalaceColor = (palace: string): string => {
  const map: Record<string, string> = {
    命宮: '#e91e63',
    財帛宮: '#ff9800',
    夫妻宮: '#e91e63',
    事業宮: '#1976d2',
    福德宮: '#4caf50',
    疾厄宮: '#00897b',
    遷移宮: '#7e57c2',
    交友宮: '#26a69a',
    田宅宮: '#8d6e63',
    子女宮: '#ec407a',
    兄弟宮: '#5c6bc0',
    父母宮: '#ef6c00',
  };
  return map[palace] ?? '#757575';
};
